import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { SalesGraph } from '../../../data/sales-graph';
import moment from 'moment';
import { User } from 'projects/core/src/lib/login/data/user';
import { UIChart } from 'primeng/chart';
import { Subscription, Subject } from 'rxjs';
import { ReportsService } from '../../../data/reports.service';
import { DatePeriod } from 'projects/app-misc/src/app/modules/reports/view/graphs-view/graphs-view.component';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LineChartStore } from '../../../data/line-chart.store';
import { LineChartQuery } from '../../../data/line-chart.query';
import { LocalStorage } from 'ngx-webstorage';
import { RxJSUnsubscribe } from 'projects/core/src/lib/core/utils/decorators/rxjs-unsubscribe-decorator';
import { RxJSUnsubscribable } from 'projects/core/src/lib/core/utils/interfaces/rxjs-unsubscribable';
import { SaleStat } from 'projects/app-misc/src/app/modules/reports/data/sale-stat';


interface Dataset {
  label?: string,
  data: any,
  fill?: boolean;
  borderColor?: string,
  backgroundColor?: string,
  tension?: number,
  userId?: number
}


class GraphData {
  labels?: string[];
  datasets?: Dataset[];
}

@Component({
  selector: 'app-sales-graph',
  templateUrl: './sales-graph.component.html',
  styleUrls: ['./sales-graph.component.scss']
})
@RxJSUnsubscribe
export class SalesGraphComponent implements OnInit, OnDestroy, RxJSUnsubscribable {
  localSubj$ = new Subject<void>();

  @ViewChild('chartLine', {static: true})
  uiChart: UIChart

  basicData: GraphData = {};

  basicOptions: any;

  graph: SaleStat[];
  totalGraph: SaleStat;

  datePeriod: DatePeriod;

  selectedUsers: User[];

  dateFormat: string;

  tempUserMap = new Map();

  graphLoading = false;

  @LocalStorage()
  hiddenUsersIds: number[]

  hiddenUserCounter = 0;

  updateSubj: Subscription;
  chartLoadingSubj: Subscription;
  eventsArrsSubj: Subscription;
  datePeriodSubj: Subscription;
  dateFormatSubj: Subscription;
  selectedUsersSubj: Subscription;

  private eventsSubscription: Subscription;

  constructor(
    protected reportsService: ReportsService,
    protected lineChartQuery: LineChartQuery,
    protected lineChartStore: LineChartStore
  ) {

    this.localSubj$.pipe(
      debounceTime(500),
      takeUntil(this.getUnsubFromAllSubs())
    ).subscribe(emit => {
      this.fillDates()
    })
  }

  ngOnInit(): void {

    this.serviceSub();

    this.userFilterSub();

    this.chartLoadingSubj = this.lineChartQuery.chartLoading$.subscribe(status => {

      this.graphLoading = status
    })
  }

  serviceSub() {
    this.eventsArrsSubj = this.lineChartQuery.events$.subscribe(data => {
      this.graph = [...data]

      if(this.graph) {
        //Pop Acadia total report
        this.totalGraph = this.graph.pop()
      }

      if(!this.localSubj$.closed) {
        this.localSubj$.next();
      }
    })

    this.datePeriodSubj = this.lineChartQuery.datePeriod$.subscribe(data => {
      this.datePeriod = data
    })


    this.dateFormatSubj = this.lineChartQuery.dateFormat$.subscribe(date => {
      this.dateFormat = date
    })

    this.selectedUsersSubj = this.lineChartQuery.selectedUsers$.subscribe(data => {
      this.selectedUsers = data
    })
  }

  fillDates() {

    this.basicData = {}
    this.basicData.labels = []
    this.basicData.datasets = [];

    this.countUsersReport();
    this.countAcadiaReport();

    if(this.selectedUsers.length === 0) {
      this.basicData.datasets.push({
        label: 'No User',
        data: [],
        borderColor: '#444',
        fill: false,
        tension: .0,
        userId: -1
      })
    }

    this.updateInfo()

    // this.graphLoading = false;

    this.lineChartStore.updateLoading(false);
  }

  countUsersReport() {
    if(this.graph) {
      const balancesArrMap = new Map()
      const totalArrMap = new Map()

      this.graph.forEach(userReport => {
        userReport.periods.forEach(period => {
          if(!balancesArrMap.has(userReport.salesUser.name)) {
            balancesArrMap.set(userReport.salesUser.name, [])
            totalArrMap.set(userReport.salesUser.name, [])
          }

          balancesArrMap.get(userReport.salesUser.name).push({
            x: moment(period.startDate).format(this.dateFormat),
            y: period.invoicedBalance
          })
          totalArrMap.get(userReport.salesUser.name).push({
            x: moment(period.startDate).format(this.dateFormat),
            y: period.totalAmt
          })

        })
      })

      this.selectedUsers.forEach(user => {
        const paidInfo = {
          label: user.name + '(Paid)',
          data: balancesArrMap.get(user.name),
          backgroundColor: this.hexToRgbA('#' + user.bgColor),
          borderColor: '#' + user.bgColor,
          fill: true,
          tension: .0,
          userId: user.id
        }

        const totalInfo = {
          label: user.name + '(Total)',
          data: totalArrMap.get(user.name),
          borderColor: '#' + user.bgColor,
          fill: false,
          tension: .0,
          userId: user.id
        }

        this.basicData.datasets.push(paidInfo)
        this.basicData.datasets.push(totalInfo)

        this.tempUserMap.set(user.id, [paidInfo, totalInfo])
      })

      this.basicOptions = {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: `${this.datePeriod.label} - Sales by Sales Rep (Cash Basis)`,
            fontSize: 16,
            position: 'bottom'
          },
          legend: {
            display: false
          }
        }
      }
    }
  }

  countAcadiaReport() {

    if(this.totalGraph) {
      const balances = [];
      const totals = []

      this.totalGraph.periods.forEach(period => {
        balances.push({
          x: moment(period.startDate).format(this.dateFormat),
          y: period.invoicedBalance
        })
        totals.push({
          x: moment(period.startDate).format(this.dateFormat),
          y: period.totalAmt
        })
      })

      const paidInfo = {
        label: 'Acadia Craft (Paid)',
        data: balances,
        backgroundColor: this.hexToRgbA('#ff9f1a'),
        borderColor: '#ff9f1a',
        fill: true,
        tension: .0,
        userId: -1
      }

      const totalInfo = {
        label: 'Acadia Craft (Total)',
        data: totals,
        borderColor: '#ff9f1a',
        fill: false,
        tension: .0,
        userId: -1
      }


      this.basicData.datasets.push(paidInfo)
      this.basicData.datasets.push(totalInfo)

      this.tempUserMap.set(-1, [paidInfo, totalInfo])
    }
  }

  hexToRgbA(hex: string) {
    let c: any;
    if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('');
      if(c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = '0x' + c.join('');
      // tslint:disable-next-line: no-bitwise
      const result = 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',0.4)'
      return result;
    }
    throw new Error('Bad Hex');
  }

  userFilterSub() {
    this.updateSubj = this.reportsService.userFilter$.pipe(takeUntil(this.getUnsubFromAllSubs())).subscribe(userId => {

      if(this.basicData.datasets) {
        const i = this.basicData.datasets.findIndex(data => data.userId === userId);

        if(this.basicData.datasets) {
          if(i >= 0) {
            this.basicData.datasets.splice(i, 2)
          } else {
            if(this.tempUserMap.has(userId)) {
              this.basicData.datasets = this.basicData.datasets.concat(this.tempUserMap.get(userId))
            }
          }
        }
        if(this.uiChart) {
          this.uiChart.reinit()
        }
      }
    })

  }

  updateInfo() {
    if(this.hiddenUsersIds) {
      this.hiddenUsersIds.forEach(id => {
        this.reportsService.userFilter$.next(id)
      })
    }
  }

  ngOnDestroy() {
    this.localSubj$.unsubscribe();
    this.updateSubj.unsubscribe();
    this.eventsArrsSubj.unsubscribe();
    this.dateFormatSubj.unsubscribe();
    this.datePeriodSubj.unsubscribe();
    this.chartLoadingSubj.unsubscribe();
    this.selectedUsersSubj.unsubscribe();
  }

  getUnsubFromAllSubs(): Subject<boolean> {
    return null
  }
}
